/* -------------------------------------------------------------
 * assets/mania-home.js
 * Geteiltes JS für die neuen Startseiten-Sections (home-*.liquid):
 * Reveal-on-Scroll (.mh-rv/.mh-rv-x/.mh-rv-xl -> .mh-in) und ein
 * wiederverwendbarer Zähl-Helfer (window.mhCountUp) für Kennzahlen,
 * dessen Beobachtung (Elemente mit [data-mh-count]) hier ebenfalls
 * automatisch läuft -- eine Section muss dafür KEIN eigenes Inline-
 * <script> mehr mitbringen, das window.mhObserveCounters manuell
 * aufruft (das führte zu einem Race: das Inline-Script einer
 * Section lief synchron beim Parsen, dieses Modul aber -- weil
 * type="module" -- erst deferred danach, wodurch
 * window.mhObserveCounters zum Zeitpunkt des Aufrufs oft noch gar
 * nicht existierte und die Zähler bei "0" hängen blieben).
 * Als type="module" in jeder Section eingebunden, die es braucht --
 * ES-Module werden vom Browser pro URL nur einmal ausgeführt, daher
 * unproblematisch, die <script>-Zeile mehrfach zu wiederholen.
 * ------------------------------------------------------------- */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartGroupedSections } from '@theme/events';

function mhFormatNumber(n, dec) {
  if (dec > 0) return n.toFixed(dec).replace('.', ',');
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

window.mhCountUp = function (el) {
  if (!el || el.dataset.mhCounted === '1') return;
  el.dataset.mhCounted = '1';
  var raw = el.getAttribute('data-to');
  var to = parseFloat(raw);
  if (isNaN(to)) return;
  var dec = el.getAttribute('data-dec') ? parseInt(el.getAttribute('data-dec'), 10) : raw.indexOf('.') >= 0 ? 1 : 0;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.textContent = mhFormatNumber(to, dec);
    return;
  }
  var start = null;
  var dur = 1500;
  function step(ts) {
    if (!start) start = ts;
    var p = Math.min((ts - start) / dur, 1);
    var e = 1 - Math.pow(1 - p, 3);
    el.textContent = mhFormatNumber(to * e, dec);
    if (p < 1) requestAnimationFrame(step);
    else el.textContent = mhFormatNumber(to, dec);
  }
  requestAnimationFrame(step);
};

window.mhObserveCounters = function (root) {
  var scope = root || document;
  var els = [].slice.call(scope.querySelectorAll('[data-mh-count]'));
  if (!els.length) return;
  if (!('IntersectionObserver' in window)) {
    els.forEach(window.mhCountUp);
    return;
  }
  var co = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          window.mhCountUp(entry.target);
          co.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.3, rootMargin: '0px 0px -7% 0px' }
  );
  els.forEach(function (el) {
    co.observe(el);
  });
};

(function () {
  if (window.__mhRevealInit) return;
  window.__mhRevealInit = true;

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('mh-in');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.14, rootMargin: '0px 0px -7% 0px' }
  );

  function observeAll() {
    document.querySelectorAll('.mh-rv, .mh-rv-x, .mh-rv-xl').forEach(function (el) {
      io.observe(el);
    });
    // Zähler automatisch beobachten -- kein Warten mehr auf ein
    // Inline-Script der jeweiligen Section (siehe Kommentar oben).
    window.mhObserveCounters(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }

  // Neue Sections, die per Theme-Editor nachträglich eingefügt werden.
  document.addEventListener('shopify:section:load', observeAll);
})();


/* -------------------------------------------------------------
 * Home: Saison-Bundle (sections/home-season-bundle.liquid) -- der
 * "Set sichern"-Button (".hsb__cta") ist ein echtes Multi-Add-to-
 * Cart: legt alle im Bundle gewählten Produkte in einem POST
 * /cart/add.js-Request in den Warenkorb, analog zum Bundle-Builder
 * auf der PDP (assets/mania-pdp.js). Varianten-IDs kommen aus
 * data-variant-ids (kommagetrennt, von Liquid vorgerendert).
 * ------------------------------------------------------------- */
(function () {
  var btns = [].slice.call(document.querySelectorAll('.hsb__cta'));
  if (!btns.length) return;

  function gatherGroupedSectionIds() {
    var sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return Array.from(new Set(sections));
  }

  function addBundleToCart(btn) {
    if (btn.disabled || btn.classList.contains('is-loading')) return;
    var ids = (btn.getAttribute('data-variant-ids') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!ids.length) return;
    var items = ids.map(function (id) { return { id: id, quantity: 1 }; });

    var span = btn.querySelector('span');
    var orig = span ? span.textContent : btn.textContent;
    function setLabel(t) {
      if (span) span.textContent = t;
      else btn.textContent = t;
    }

    btn.disabled = true;
    btn.classList.add('is-loading');
    setLabel('Wird hinzugefügt …');

    var sectionIds = gatherGroupedSectionIds();
    var body = JSON.stringify({
      items: items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    fetch(FoxTheme.routes.cart_add_url, fetchConfig('json', { body: body }))
      .then(function (res) {
        return res.json();
      })
      .then(function (response) {
        btn.classList.remove('is-loading');
        if (response.status) {
          btn.disabled = false;
          setLabel('Fehler – bitte erneut versuchen');
          setTimeout(function () {
            setLabel(orig);
          }, 2200);
          return;
        }
        document.dispatchEvent(
          new CartAddEvent(response, 'home-season-bundle', {
            source: 'home-season-bundle',
            itemCount: items.length,
            sections: response.sections,
          })
        );
        setLabel('Hinzugefügt ✓');
        setTimeout(function () {
          setLabel(orig);
          btn.disabled = false;
        }, 1600);
      })
      .catch(function (err) {
        console.error(err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        setLabel('Fehler – bitte erneut versuchen');
        setTimeout(function () {
          setLabel(orig);
        }, 2200);
      });
  }

  btns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      addBundleToCart(btn);
    });
  });
})();


/* -------------------------------------------------------------
 * Quick-ATC (Einzelprodukt) für die Startseiten-Sections
 * home-community-favorites.liquid (.hcf__form) und
 * home-kids-teaser.liquid (.hkt__form). Beide rendern ein
 * {% form 'product' %} mit data-mh-quick-atc -- ohne JS würde der
 * Browser auf /cart/add posten und auf /cart umleiten. Hier wird
 * der Submit abgefangen, per AJAX in den Warenkorb gelegt und das
 * Theme-eigene CartAddEvent gefeuert -> Cart Drawer öffnet sich
 * (wie bei Season-Bundle / Bundle-Builder). Delegiert am document,
 * damit auch nachträglich gerenderte Karten funktionieren. Das
 * Formular bleibt als No-JS-Fallback erhalten.
 * ------------------------------------------------------------- */
(function () {
  if (window.__mhQuickAtcBound) return;
  window.__mhQuickAtcBound = true;

  function gatherGroupedSectionIds() {
    var sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return Array.from(new Set(sections));
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mh-quick-atc')) return;
    event.preventDefault();

    var btn = form.querySelector('button[type="submit"]');
    if (btn && (btn.disabled || btn.classList.contains('is-loading'))) return;

    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput ? idInput.value : '';
    if (!variantId) return;
    var qtyInput = form.querySelector('[name="quantity"]');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }
    function reset() {
      if (!btn) return;
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }

    var sectionIds = gatherGroupedSectionIds();
    var body = JSON.stringify({
      items: [{ id: variantId, quantity: quantity }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    fetch(FoxTheme.routes.cart_add_url, fetchConfig('json', { body: body }))
      .then(function (res) {
        return res.json();
      })
      .then(function (response) {
        reset();
        if (response.status) {
          console.error('[mh-quick-atc]', response.description || response.message);
          document.dispatchEvent(
            new CustomEvent('product-ajax:error', {
              detail: { errorMessage: response.description || response.message },
            })
          );
          return;
        }
        document.dispatchEvent(
          new CartAddEvent(response, form.id || 'mh-quick-atc', {
            source: 'mh-quick-atc',
            itemCount: quantity,
            variantId: String(variantId),
            sections: response.sections,
          })
        );
      })
      .catch(function (err) {
        console.error(err);
        reset();
      });
  });
})();
